import { Color3, Mesh, MeshBuilder, StandardMaterial, Texture, VertexBuffer, type Scene } from '@babylonjs/core';
import { createLogger, resolveTileCenter, type MapBounds, type ThemeFile, type ThemeWallBand, type TileMapMetadata, type Vector2, type WorldSpace } from '@sorrem/shared';
import type { MapData } from '../map-loader';
import { applyWorldSpaceUvs, createFloorTilesForRegion } from './floor-tile-mesh';
import { worldSpaceToScenePosition } from './world-to-scene-position';
import { buildWallMeshesWithModels, loadTileCatalog } from './map-meshes-with-models';
import { buildMapProps, type MapPropBuildResult } from './map-props';
import { getMaterialsLoader } from './materials-loader';
import { createWallFeatureMeshes, type WallFeaturePart } from './wall-feature-mesh';
import { createWallProfileMesh } from './wall-profile-mesh';
import { isFloorTile } from './wall-cutaway-tiles';

const logger = createLogger('engine:map-meshes');

export interface BuildMapMeshesOptions {
    readonly tileUnitSize: number;
    /** Wall height in tile-units. */
    readonly wallHeightTileUnits?: number;
    /**
     * Builds only the tiles, wall models and props inside this region; the
     * tiles around it still shape its walls. The whole map when left out.
     */
    readonly region?: MapBounds;
}

export interface MapMeshBuildResult {
    /** Every mesh created for this map (ground tiles + walls). */
    readonly meshes: readonly Mesh[];
    /** All ground floor tile meshes (subset of `meshes`). */
    readonly floorMeshes: readonly Mesh[];
    /** All wall meshes (subset of `meshes`). */
    readonly wallMeshes: readonly Mesh[];
    /** Props the crucible was decorated with (instanced, not pickable). */
    readonly props: MapPropBuildResult;
    /** The profile walls' flat tops, which the wall cutaway lowers with a cut wall; null for other walls. */
    readonly wallCaps: WallCapMesh | null;
}

/** Flat wall tops, one quad (four vertices, in order) per wall tile. */
export interface WallCapMesh {
    /** Also in `wallMeshes`. */
    readonly mesh: Mesh;
    /** The wall tile under each quad, in vertex order. */
    readonly tiles: readonly Vector2[];
}

/**
 * Build 3D meshes for a loaded map: floor tile quads, the theme's wall models,
 * profile walls and caps, and props. Positions go through
 * `worldSpaceToScenePosition` (tile X -> scene Z, tile Y -> scene X); the fixed
 * isometric camera does the projection.
 *
 * Materials are loaded from the theme configuration and applied as tiling
 * materials for efficiency (one material per type, not per-tile).
 *
 * @param scene The game scene
 * @param mapData The loaded map data
 * @param themeData The theme configuration (`tileCatalog`, `defaults`, `walls`; see docs/theme-architecture-3d.md)
 * @param metadata The map metadata containing tile info and wall model configs
 * @param options Build options
 */
export async function buildMapMeshes(
    scene: Scene,
    mapData: MapData,
    themeData: ThemeFile,
    metadata: TileMapMetadata,
    options: BuildMapMeshesOptions,
): Promise<MapMeshBuildResult> {
    const { tileUnitSize, wallHeightTileUnits, region } = options;
    const meshes: Mesh[] = [];
    const materialsLoader = getMaterialsLoader();

    // Load tile-catalog to get material metadata. Every theme draws through
    // its catalog: floor material, wall models and floor model all come from it.
    const tileCatalogPath = themeData.tileCatalog;
    if (!tileCatalogPath) {
        throw new Error(`Theme "${themeData.id ?? 'unknown'}" has no tileCatalog; buildMapMeshes needs one.`);
    }
    const tileCatalog: Record<string, { material?: string }> = {};
    try {
        const catalogData = await loadTileCatalog(tileCatalogPath);
        // Extract material for each tile
        for (const [tileName, tileData] of Object.entries(catalogData.tiles ?? {})) {
            if (tileData.material) {
                tileCatalog[tileName] = { material: tileData.material };
            }
        }
    } catch (error) {
        logger.warn('Failed to load tile-catalog:', error);
    }

    // The floor the crucible picked, or the theme's floor tile material.
    const floorMaterialId = resolveFloorMaterialId(metadata, themeData, tileCatalog);
    let floorMaterial: StandardMaterial | null = null;
    try {
        if (floorMaterialId) {
            floorMaterial = await materialsLoader.loadMaterial(floorMaterialId, scene);
        }
    } catch (error) {
        logger.warn(`Failed to load floor material "${floorMaterialId}":`, error);
    }

    if (!floorMaterial) {
        const floorMeshName = themeData.defaults?.['floor']?.mesh;
        throw new Error(
            `Unable to load floor material ${floorMaterialId ? `"${floorMaterialId}" ` : ''}for theme "${themeData.id ?? 'unknown'}". ` +
            `The map's floorMaterial or the tile-catalog entry for mesh "${floorMeshName}" must name a material in materials.json. ` +
            `Available tiles: ${Object.keys(tileCatalog).join(', ')}`,
        );
    }

    // Create individual floor tile meshes with tiling material
    const floorResult = createFloorTilesForRegion(scene, mapData, {
        tileUnitSize,
        material: floorMaterial,
        region,
    });
    meshes.push(...floorResult.meshes);

    // Wall models from the persisted per-tile assignments (3D auto-tiling).
    const wallMeshResult = await buildWallMeshesWithModels(scene, metadata, themeData, {
        tileUnitSize,
        wallHeightTileUnits,
        floorTiles: themeData.floorModel ? collectFloorTiles(mapData, region ?? mapData.bounds) : undefined,
        region,
    });
    meshes.push(...wallMeshResult.wallMeshes);
    if (wallMeshResult.floorMeshes.length > 0) {
        // The floor model draws the floor; the flat tiles stay for picking only.
        hideForPickingOnly(scene, floorResult.meshes);
        meshes.push(...wallMeshResult.floorMeshes);
    }
    const floorMeshes = [...floorResult.meshes, ...wallMeshResult.floorMeshes];
    const { meshes: profileWalls, capMaterial: profileMaterial } = await buildProfileWalls(scene, mapData, themeData, tileUnitSize, region);
    meshes.push(...profileWalls);
    const wallFeatures = await buildWallFeatures(scene, metadata, themeData, tileUnitSize, region);
    meshes.push(...wallFeatures);
    const caps = buildWallCaps(scene, mapData, themeData, tileUnitSize, profileMaterial, region ?? mapData.bounds);
    const capMeshes = caps ? [caps.mesh] : [];
    meshes.push(...capMeshes);
    const props = await buildMapProps(scene, mapData.objectLayers, tileUnitSize, region);
    return {
        meshes,
        floorMeshes,
        wallMeshes: [...wallMeshResult.wallMeshes, ...profileWalls, ...wallFeatures, ...capMeshes],
        props,
        wallCaps: profileMaterial && caps ? caps : null,
    };
}

/**
 * The material id the floor is drawn with: the one the crucible picked when
 * it was generated (`assets.floorMaterial`), or else the material of the
 * theme's floor tile in its catalog, for maps built before floors were picked.
 */
export function resolveFloorMaterialId(
    metadata: TileMapMetadata,
    themeData: ThemeFile,
    tileCatalog: Readonly<Record<string, { readonly material?: string }>>,
): string | undefined {
    if (metadata.assets.floorMaterial) {
        return metadata.assets.floorMaterial;
    }
    const floorMeshName = themeData.defaults?.['floor']?.mesh;
    return floorMeshName ? tileCatalog[floorMeshName]?.material : undefined;
}

/** `region` (the whole wall layer when left out) cut to the wall layer. */
function clampToWalls(mapData: MapData, region: MapBounds | undefined): MapBounds {
    return {
        minX: Math.max(0, region?.minX ?? 0),
        minY: Math.max(0, region?.minY ?? 0),
        maxX: Math.min(mapData.wallWidth, region?.maxX ?? mapData.wallWidth),
        maxY: Math.min(mapData.wallHeight, region?.maxY ?? mapData.wallHeight),
    };
}

/** Floor tiles in `region` that are not under a wall, for the theme's floor model. */
function collectFloorTiles(mapData: MapData, region: MapBounds): WorldSpace[] {
    const { bounds } = mapData;
    const tiles: WorldSpace[] = [];
    for (let y = Math.max(bounds.minY, region.minY); y < Math.min(bounds.maxY, region.maxY); y += 1) {
        for (let x = Math.max(bounds.minX, region.minX); x < Math.min(bounds.maxX, region.maxX); x += 1) {
            if (isFloorTile(mapData, x, y)) {
                tiles.push({ x, y });
            }
        }
    }
    return tiles;
}

// Materials made here rather than loaded, one per scene: a map is built a
// chunk at a time, and a material per chunk would pile up, since a map's
// meshes are disposed without their (shared) materials.
const pickOnlyMaterials = new WeakMap<Scene, StandardMaterial>();
const capMaterials = new WeakMap<Scene, Map<string, StandardMaterial>>();

/**
 * Keeps meshes in the scene and pickable but draws nothing for them: they
 * write neither color nor depth. Picking ignores materials, and debug views
 * that toggle `isVisible` on floor meshes still work.
 */
function hideForPickingOnly(scene: Scene, meshes: readonly Mesh[]): void {
    let material = pickOnlyMaterials.get(scene);
    if (!material) {
        material = new StandardMaterial('floor-pick-only', scene);
        material.disableColorWrite = true;
        material.disableDepthWrite = true;
        pickOnlyMaterials.set(scene, material);
    }
    for (const mesh of meshes) {
        mesh.material = material;
    }
}

/** Sweeps the theme's wall profile along every wall face in `region`, when it sets one. */
async function buildProfileWalls(
    scene: Scene,
    mapData: MapData,
    themeData: ThemeFile,
    tileUnitSize: number,
    region: MapBounds | undefined,
): Promise<{ readonly meshes: Mesh[]; readonly capMaterial: StandardMaterial | null }> {
    const profile = themeData.walls?.profile;
    if (!profile) {
        return { meshes: [], capMaterial: null };
    }
    const load = async (id: string): Promise<StandardMaterial | null> => {
        const material = await getMaterialsLoader().loadMaterial(id, scene);
        if (!material) {
            logger.warn(`Wall material "${id}" did not load; that part of the walls is untextured`);
        }
        return material;
    };
    // One mesh per material, so each band can carry its own texture and the cutaway clips them all.
    const meshes: Mesh[] = [];
    for (const group of groupProfileSegments(profile.points, profile.material, profile.bands)) {
        const material = await load(group.material);
        const mesh = createWallProfileMesh(scene, mapData, {
            tileUnitSize,
            profile: profile.points,
            material,
            region,
            segments: group.segments,
            includeSteps: group.includeSteps,
        });
        if (mesh) {
            mesh.name = `wall-profiles:${group.material}`;
            meshes.push(mesh);
        }
    }
    const capMaterial = await load(themeData.walls?.capMaterial ?? profile.material);
    return { meshes, capMaterial };
}

/**
 * The material each wall feature part is drawn in, from the theme's profile
 * walls: the base and niche in the plinth (the band at the floor), pilaster
 * and pier bodies in the theme's dressed `featureMaterial` (else the wall),
 * alcove frames in the wall, their tops in the cap. Undefined without a profile.
 */
export function resolveWallFeatureMaterialIds(themeData: ThemeFile): Record<WallFeaturePart, string> | undefined {
    const profile = themeData.walls?.profile;
    if (!profile) {
        return undefined;
    }
    const plinth = profile.bands?.find((band) => band.from <= 0 && band.to > 0)?.material ?? profile.material;
    return {
        base: plinth,
        body: themeData.walls?.featureMaterial ?? profile.material,
        frame: profile.material,
        top: themeData.walls?.capMaterial ?? profile.material,
        niche: plinth,
    };
}

/**
 * How far each wall feature part's texture runs ahead of plain height, so
 * its courses line up with the wall's: the profile walls measure v along
 * their outline, so for each part this is the outline length at the foot of
 * the first upright profile segment drawn in that part's material, less its
 * height. A part whose material has no upright segment adds none. The body
 * always follows the wall's own courses, whatever stone it is drawn in.
 */
export function resolveWallFeatureTextureOffsets(
    profile: { readonly points: ReadonlyArray<readonly [number, number]>; readonly material: string; readonly bands?: ReadonlyArray<ThemeWallBand> },
    materialIds: Readonly<Record<WallFeaturePart, string>>,
): Record<WallFeaturePart, number> {
    const { points } = profile;
    const materialOf = new Map<number, string>();
    for (const group of groupProfileSegments(points, profile.material, profile.bands)) {
        for (const segment of group.segments) {
            materialOf.set(segment, group.material);
        }
    }
    const offsetFor = (material: string): number => {
        let length = 0;
        for (let segment = 0; segment + 1 < points.length; segment += 1) {
            const [outA, heightA] = points[segment]!;
            const [outB, heightB] = points[segment + 1]!;
            if (outA === outB && heightB > heightA && materialOf.get(segment) === material) {
                return length - heightA;
            }
            length += Math.hypot(outB - outA, heightB - heightA);
        }
        return 0;
    };
    return {
        base: offsetFor(materialIds.base),
        body: offsetFor(profile.material),
        frame: offsetFor(profile.material),
        top: offsetFor(materialIds.top),
        niche: offsetFor(materialIds.niche),
    };
}

/** The theme's pilasters, alcoves and corner pilasters in `region`, one mesh per part. */
async function buildWallFeatures(
    scene: Scene,
    metadata: TileMapMetadata,
    themeData: ThemeFile,
    tileUnitSize: number,
    region: MapBounds | undefined,
): Promise<Mesh[]> {
    const ids = resolveWallFeatureMaterialIds(themeData);
    const features = metadata.wallFeatures ?? [];
    if (!ids || features.length === 0 || !themeData.walls) {
        return [];
    }
    const loader = getMaterialsLoader();
    const materials: Record<WallFeaturePart, StandardMaterial | null> = {
        base: await loader.loadMaterial(ids.base, scene),
        body: await loader.loadMaterial(ids.body, scene),
        frame: await loader.loadMaterial(ids.frame, scene),
        top: await loader.loadMaterial(ids.top, scene),
        niche: await loader.loadMaterial(ids.niche, scene),
    };
    const textureOffsets = resolveWallFeatureTextureOffsets(themeData.walls.profile!, ids);
    // A dressed feature stone carries painted grout at its edges: fit one repeat to each face (its span is 1 / uScale).
    const bodyTexture = materials.body?.diffuseTexture;
    const bodyScale = themeData.walls.featureMaterial && bodyTexture instanceof Texture ? bodyTexture.uScale : undefined;
    const bodyFaceSpan = bodyScale ? 1 / bodyScale : undefined;
    return createWallFeatureMeshes(scene, features, {
        tileUnitSize,
        wallHeight: themeData.walls.height,
        materials,
        textureOffsets,
        ...(bodyFaceSpan ? { bodyFaceSpan } : {}),
        ...(bodyFaceSpan && themeData.walls.featureCourses ? { bodyCourses: themeData.walls.featureCourses } : {}),
        ...(region ? { region } : {}),
    });
}

/** A set of wall profile segments drawn in one material (see `groupProfileSegments`). */
export interface WallProfileSegmentGroup {
    readonly material: string;
    readonly segments: number[];
    /** Whether this group carries the step faces between wall tiles: the wall material's group does. */
    readonly includeSteps: boolean;
}

/**
 * Splits a wall profile's segments by material: each segment takes the
 * material of the band its middle height lies in, or `wallMaterial` outside
 * every band. Groups come in the order their material first appears.
 */
export function groupProfileSegments(
    points: ReadonlyArray<readonly [number, number]>,
    wallMaterial: string,
    bands: ReadonlyArray<ThemeWallBand> | undefined,
): WallProfileSegmentGroup[] {
    const groups = new Map<string, WallProfileSegmentGroup>();
    for (let segment = 0; segment + 1 < points.length; segment += 1) {
        const middle = (points[segment]![1] + points[segment + 1]![1]) / 2;
        const material = bands?.find((band) => middle >= band.from && middle < band.to)?.material ?? wallMaterial;
        const group = groups.get(material) ?? { material, segments: [], includeSteps: material === wallMaterial };
        group.segments.push(segment);
        groups.set(material, group);
    }
    return [...groups.values()];
}

const CAP_THICKNESS = 0.06;

/**
 * Face-drawn walls are panels on the sides of a wall tile, so the tile's top
 * is open. This lays one flat cap over every wall tile in `region` at the
 * theme's wall height, merged into a single mesh. Themes with per-tile wall
 * models need none.
 * With profile walls, the caps take the walls' `capMaterial` (else the profile's material), mapped from above
 * in world tiles, so the tops match the plinth and cornice; otherwise they
 * are a flat `capColor`. The merged quads keep their tiles' order, four
 * vertices each, so the wall cutaway can lower one tile's cap.
 */
function buildWallCaps(
    scene: Scene,
    mapData: MapData,
    themeData: ThemeFile,
    tileUnitSize: number,
    profileMaterial: StandardMaterial | null,
    region: MapBounds,
): WallCapMesh | null {
    const walls = themeData.walls;
    if (walls?.mode !== 'faces' || !walls.height) {
        return null;
    }
    const pieces: Mesh[] = [];
    const tiles: Vector2[] = [];
    const wallRegion = clampToWalls(mapData, region);
    for (let y = wallRegion.minY; y < wallRegion.maxY; y += 1) {
        for (let x = wallRegion.minX; x < wallRegion.maxX; x += 1) {
            if ((mapData.wallGids[y * mapData.wallWidth + x] ?? 0) === 0) {
                continue;
            }
            // Profile walls already draw the top edge (the parapet), so their
            // caps are flat tiles; a box's sides would overlap it.
            const cap = profileMaterial
                ? MeshBuilder.CreateGround(`wall-cap-${x}-${y}`, { width: tileUnitSize, height: tileUnitSize }, scene)
                : MeshBuilder.CreateBox(`wall-cap-${x}-${y}`, { width: tileUnitSize, height: CAP_THICKNESS, depth: tileUnitSize }, scene);
            const capY = walls.height * tileUnitSize - (profileMaterial ? 0 : CAP_THICKNESS / 2);
            const center = worldSpaceToScenePosition(resolveTileCenter(x, y), tileUnitSize, capY);
            cap.position.set(center.x, center.y, center.z);
            pieces.push(cap);
            tiles.push({ x, y });
        }
    }
    if (pieces.length === 0) {
        return null;
    }
    const merged = Mesh.MergeMeshes(pieces, true, true);
    if (!merged) {
        throw new Error('buildMapMeshes: failed to merge wall caps');
    }
    merged.name = 'wall-caps';
    if (profileMaterial) {
        applyWorldSpaceUvs(merged, tileUnitSize);
        // Updatable, so the wall cutaway can move a cut tile's cap down.
        merged.setVerticesData(VertexBuffer.PositionKind, merged.getVerticesData(VertexBuffer.PositionKind)!, true);
        merged.material = profileMaterial;
        return { mesh: merged, tiles };
    }
    const capColor = walls.capColor ?? '#3a3530';
    const materials = capMaterials.get(scene) ?? new Map<string, StandardMaterial>();
    capMaterials.set(scene, materials);
    let material = materials.get(capColor);
    if (!material) {
        material = new StandardMaterial('wall-caps', scene);
        material.diffuseColor = Color3.FromHexString(capColor);
        material.specularColor = Color3.Black();
        materials.set(capColor, material);
    }
    merged.material = material;
    return { mesh: merged, tiles };
}
